import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AppSetting {
  id: string;
  category: string;
  key: string;
  value: any;
  default_value: any;
  display_name: string;
  description: string | null;
  setting_type: string; // text, boolean, number, select, file
  options: any; // for select options (array)
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export function useAppSettings() {
  const [settings, setSettings] = useState<AppSetting[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from("app_settings")
      .select("*")
      .order("category", { ascending: true })
      .order("display_name", { ascending: true });

    if (error) {
      setError(error.message);
      setSettings([]);
    } else {
      setSettings((data as AppSetting[]) || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = useCallback(async (rows: { id: string; value: any }[]) => {
    if (!rows || rows.length === 0) return { success: true } as const;
    setSaving(true);
    setError(null);
    const { error } = await supabase
      .from("app_settings")
      .upsert(rows as any, { onConflict: "id" });

    if (error) {
      setError(error.message);
      setSaving(false);
      return { success: false as const, error: error.message };
    }

    // Refresh list after saving
    await fetchSettings();
    setSaving(false);
    return { success: true as const };
  }, [fetchSettings]);

  const resetAllToDefaults = useCallback(async () => {
    setSaving(true);
    setError(null);
    
    // Build array of all settings with their default values
    const resetRows = settings.map(setting => ({
      id: setting.id,
      value: setting.default_value,
      category: setting.category,
      key: setting.key,
      display_name: setting.display_name,
      description: setting.description,
      setting_type: setting.setting_type,
      options: setting.options,
      is_public: setting.is_public,
      default_value: setting.default_value,
    }));

    const { error } = await supabase
      .from("app_settings")
      .upsert(resetRows as any, { onConflict: "id" });

    if (error) {
      setError(error.message);
      setSaving(false);
      return { success: false as const, error: error.message };
    }

    // Refresh list after reset
    await fetchSettings();
    setSaving(false);
    return { success: true as const };
  }, [settings, fetchSettings]);

  return {
    settings,
    loading,
    saving,
    error,
    refresh: fetchSettings,
    saveSettings,
    resetAllToDefaults,
    setSettings, // exposed for advanced use if needed
  } as const;
}
